import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCard, Loader2, Check, AlertTriangle, ChevronLeft, Home, Receipt,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';
import { PAYMENT_METHODS, paymentMethodNote } from '../services/PaymentMethods';
import { btn } from '../../utils/buttonStyles';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import PaymentEmbeddedCheckout from './PaymentEmbeddedCheckout';
import PaymentRegistrantSelect from './PaymentRegistrantSelect';
import PaymentAdditionalInfo, { missingRequired } from './PaymentAdditionalInfo';
import PaymentAgreements, { agreementsSatisfied } from './PaymentAgreements';

/**
 * Checkout as a page of its own, rather than a dialog the redirect destroys.
 *
 * A dialog cannot survive leaving for stripe.com: the app is torn down on the
 * web and, in the native shell, comes back to whatever screen happened to be
 * underneath. Everything that made the old flow feel unfinished followed from
 * that — a back press landing on a list with nothing said, a receipt shown for
 * a payment that had not happened, a return URL nobody could reason about.
 *
 * A page has an address. Stripe is handed THIS page as both its success and its
 * cancel destination, so coming back is an ordinary navigation rather than a
 * state restoration, and every outcome is drawn by the same component that sent
 * the payer out.
 *
 * ─── WHAT THE CALLER PASSES ─────────────────────────────────────────────────
 *
 *   orderId    an order that already exists (open play raises one when it holds
 *              the court, so passing items instead would bill twice)
 *   items      [{ subjectType, subjectId }] to be priced and raised into an order
 *   user       who is paying, for the summary — this component never asks again
 *   onSuccess  called ONCE, before the payer is offered anywhere to go
 *   onFailure  called when a payment did not complete. Deliberately NOT the
 *              success path: nothing is settled, nothing is consumed, and the
 *              payer keeps their place and their choice of method
 *   backTo     where "back" goes; the page they came from
 *
 * ─── ONE SCREEN ─────────────────────────────────────────────────────────────
 *
 * The dialog asked in three steps — registrant, method, review. Registrant is
 * decided before anyone reaches a payment page, and review is what the summary
 * already is, so what is left is: here is what you are buying, here is how you
 * can pay, pay. Everything is visible at once because there is nothing to
 * progressively disclose.
 */

const money = (n, currency = 'USD') => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(v);
  } catch { return `${currency} ${v.toFixed(2)}`; }
};

/**
 * What card payment adds, as the SERVER computes it.
 *
 * Read from the order rather than recomputed here. The fee rule lives on the
 * price row, which an order does not carry — so the old client-side arithmetic
 * silently produced zero for every existing order, and the page promised
 * "Pay $18.00" while Stripe was about to take $18 plus the fee.
 *
 * A quote carries the rule inline; an order carries the answer. Accept both.
 */
const cardFeeOf = (order) => {
  if (order?.cardFee != null) return Number(order.cardFee) || 0;
  const lines = order?.lines || order?.items || [];
  return lines.reduce((sum, line) => {
    const fee = line?.bookingFee;
    if (!fee) return sum;
    const extra = fee.type === 'percent'
      ? (Number(line.amount) * Number(fee.value)) / 100
      : Number(fee.value);
    return Number.isFinite(extra) && extra > 0 ? sum + extra : sum;
  }, 0);
};

export default function PaymentCheckoutFlow({
  /**
   * The orders being paid for.
   *
   * Usually one. Several when a payer registered several entrants in one go:
   * they are one order each, because that separation is what lets any single
   * one of them be withdrawn later without unpicking the rest. They are paid
   * together — one card, one Stripe charge — and settled together.
   *
   * `orderIds[0]` still stands in wherever the flow legitimately acts on a
   * single order, but nothing is PAID from it alone any more: paying only the
   * first, while raising an order for each, is exactly how two of three
   * entrants ended up registered and unpaid with the payer told they were done.
   */
  orderIds = [],
  /**
   * A registration rather than an order — a tournament entry, a programme place
   * already on a roster. Priced by the server when the session is raised, so
   * `amount` is only what to SHOW; nobody is charged from it.
   */
  kind = 'order',
  registrationIds = null,
  amount = null,
  items = [],
  title = 'Checkout',
  user = null,
  allowCard = true,
  onSuccess,
  onFailure,
  backTo = null,
  onBack,
  // Used once the payment has landed: there is nothing to give back.
  onBackAfterPaying,
  onHome,
  /**
   * Ask who this is for before taking the money.
   *
   * Off for an order that already exists — open play holds a place for a named
   * player before checkout is ever reached, and asking again there would offer
   * to change something already decided and paid against.
   */
  collectRegistrant = false,
  onRegistrantChange,
  /** Who already holds a place — shown, but not selectable. */
  takenIds = [],
  provisionalIds = [],
  /**
   * The seller's own questions, asked as step two.
   *
   *   [{ id, label, type, required?, options?, helpText? }]
   *
   * Passed in rather than known, because every academy asks something
   * different — an emergency contact for a junior programme, a shirt size, a
   * medical note. No fields, no step.
   */
  infoFields = [],
  onInfoChange,
  infoAskedBy = null,
  /**
   * What must be signed, if anything.
   *   { requireTerms, requireWaiver, termsPath, waiverPath }
   * No requirement, no step.
   */
  agreement = null,
  subjectType = null,
  subjectId = null,
}) {
  // One order is the ordinary case and most of this flow reads in the singular,
  // so it keeps a name. Declared here because the loading effect's dependency
  // list is evaluated during render, and a const read before its own line is a
  // temporal dead zone, not a null.
  const orderId = orderIds[0] || null;

  const { theme } = useTheme();
  const { embeddedCheckout: embeddedEnabled } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();

  // Set once the payer has chosen card and the order exists, which is when
  // Stripe can be mounted. Holds the order id rather than a boolean because
  // the embedded session is built from it.
  const [embeddedOrderIds, setEmbeddedOrderIds] = useState(null);

  // STEP ONE: who this is for.
  //
  // Asked here rather than by each list that sells something, so the question
  // is worded once and behaves once. `null` means the payer themselves, which
  // is also the answer whenever nobody is asked.
  const [step, setStep] = useState('method');
  // Whether the payer has moved themselves. Until they have, the flow is free
  // to re-pin the first step — which it must, because what the steps ARE is
  // discovered after the first render: the page has to read the order before it
  // knows whether to ask who this is for, or what else to ask. Computed once,
  // the flow opened on "how you will pay" with the earlier steps already ticked.
  const [touched, setTouched] = useState(false);
  // undefined until the list reports one — null is a real answer meaning "the
  // payer themselves", so it cannot double as "not chosen yet".
  /**
   * Null means the payer themselves, which is what the picker shows selected
   * from the moment it renders.
   *
   * It used to start as `undefined`, and the two disagreed: the picker reads
   * `value ?? 'self'` so it drew the tick against My account, while Continue
   * was gated on `registrantId === undefined` and stayed greyed out. The screen
   * said a choice had been made and the button said it had not, and the only
   * way through was to click the option that already looked chosen.
   *
   * Starting at the value the picker is already displaying makes them agree.
   * A registrant who cannot be registered is still handled — the picker moves
   * off a disabled option and reports the move upward.
   */
  const [registrantId, setRegistrantId] = useState(null);
  const [registrantLabel, setRegistrantLabel] = useState(null);

  /**
   * Save what has been collected onto the order, then move on.
   *
   * The enrolment does not exist yet — it is written when the payment succeeds
   * — so the registrant, the answers and the signature have to live somewhere
   * the settle path can read them. That is the order's own pending_payload.
   *
   * Saved at each step rather than once at the end: a payer who gets as far as
   * Stripe and closes the tab has still answered, and the webhook can still
   * finish the job.
   */
  const advance = async (id, patch) => {
    setTouched(true);
    if (orderId && patch) {
      await paymentsAPI.updateOrderPayload(orderId, patch)
        .catch(err => setError(err?.message || 'Could not save your answers'));
    }
    setStep(id);
  };
  const [info, setInfo] = useState({});
  const [signed, setSigned] = useState({ termsAccepted: false, waiverAccepted: false, acceptedName: '' });
  const needsSigning = !!(agreement?.requireTerms || agreement?.requireWaiver);
  const [embeddedRegistration, setEmbeddedRegistration] = useState(null);

  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);
  // Card unless this academy does not take them, in which case the selection
  // must not start on an option the list does not offer — the summary would
  // price a card fee nobody can pay, and Pay would raise a Stripe session the
  // server refuses.
  const [method, setMethod]   = useState(allowCard ? 'card' : 'cash');

  // 'paying' | 'paid' | 'unpaid' — what the payer is looking at. Kept separate
  // from `error`, which is this app failing rather than a payment not going
  // through; the two read very differently to someone who has just tried to buy.
  const [outcome, setOutcome] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const paymentParam = searchParams.get('payment');
  const sessionId    = searchParams.get('session_id');

  // Callbacks are fired from an effect that must not re-run when the parent
  // rebuilds them, so they are read through a ref rather than depended on.
  const cb = useRef({ onSuccess, onFailure });
  cb.current = { onSuccess, onFailure };

  const methodLabel = (PAYMENT_METHODS.find(m => m.id === method) || {}).label || 'Not chosen';
  const payingForLabel = registrantLabel
    || user?.name
    || [user?.firstName, user?.lastName].filter(Boolean).join(' ')
    || null;

  // Cards can be switched off after this component has mounted, once the
  // academy resolves. Move off card rather than leaving it selected-but-absent.
  useEffect(() => {
    if (!allowCard && method === 'card') setMethod('cash');
  }, [allowCard, method]);

  const methods = useMemo(
    () => PAYMENT_METHODS
      .filter(m => allowCard || m.id !== 'card')
      .map(m => ({ id: m.id, Icon: m.Icon, label: m.label, hint: paymentMethodNote(m.id, 'a coach') })),
    [allowCard],
  );

  // ─── What is being bought ────────────────────────────────────────────────
  useEffect(() => {
    let dropped = false;
    // A registration has no order to read. Ask the server what it owes rather
    // than trusting a number passed in a URL — a total anyone can edit is not a
    // total worth showing, and the caller's copy may be stale anyway.
    if (!orderId && registrationIds?.length) {
      setLoading(true);
      paymentsAPI.checkRegistrations({ kind, registrationIds })
        .then((res) => {
          if (dropped) return;
          const owed = res?.results?.[0]?.amount;
          setOrder({
            lines: [{ id: 'reg', label: title, amount: Number(owed ?? amount) || 0 }],
            currency: 'USD',
          });
          setError(null);
        })
        .catch(() => {
          if (dropped) return;
          // Fall back to what the caller believed, so the payer still sees a
          // figure; the server prices the session regardless.
          setOrder({ lines: [{ id: 'reg', label: title, amount: Number(amount) || 0 }], currency: 'USD' });
        })
        .finally(() => { if (!dropped) setLoading(false); });
      return;
    }
    setLoading(true);

    // SEVERAL ORDERS, ONE BASKET.
    //
    // Each entrant has their own order, and the payer is looking at one screen
    // and one total. So the lines are pooled — every order contributes its own,
    // already labelled with who it is for — and the card fee is taken from the
    // orders that quote one rather than added up per order, because it is
    // charged once for the payment, not once per person on it.
    const load = orderIds.length
      ? Promise.all(orderIds.map(id => paymentsAPI.getOrder(id))).then(orders => ({
        lines: orders.flatMap(o => (o?.items || o?.lines || [])),
        currency: orders[0]?.currency || 'USD',
        cardFee: orders.reduce((most, o) => Math.max(most, Number(o?.cardFee ?? 0)), 0),
      }))
      : paymentsAPI.quoteBasket({ items });

    load
      .then((res) => { if (!dropped) { setOrder(res); setError(null); } })
      .catch((err) => { if (!dropped) setError(err?.message || 'Could not price this'); })
      .finally(() => { if (!dropped) setLoading(false); });
    return () => { dropped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(orderIds), JSON.stringify(items), JSON.stringify(registrationIds), amount, title]);

  const lines    = order?.lines || order?.items || [];
  const currency = order?.currency || 'USD';
  const subtotal = lines.reduce((t, l) => t + Number(l.amount || 0), 0);
  const fees     = method === 'card' ? Math.round(cardFeeOf(order) * 100) / 100 : 0;
  const total    = subtotal + fees;

  /**
   * Nothing to pay.
   *
   * A programme with no price, a night covered by a membership, a discount
   * that lands on zero — the total is a property of THIS basket for THIS
   * person, not of the thing being bought, so it is decided here rather than
   * anywhere upstream. When it is zero there is no Stripe session and no
   * counter payment either, so the whole question of how you will pay stops
   * being a question and the step goes away. Everything before it — who it is
   * for, the answers, the waiver — is unchanged, because none of that was
   * ever about money.
   */
  const nothingToPay = lines.length > 0 && total === 0;

  /** Where the last form step hands off, now that 'method' may not exist. */
  const nextAfterForms = nothingToPay ? 'review' : 'method';

  // ─── Coming back from Stripe ─────────────────────────────────────────────
  //
  // The truth comes from the server re-reading the session, never from the
  // query string — "?payment=success" is something anyone can type.
  useEffect(() => {
    if (!paymentParam) return;

    const strip = () => setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('payment');
      next.delete('session_id');
      return next;
    }, { replace: true });

    // Stripe's own back link. Nothing was charged and nothing is settled, so
    // the callback stays unfired and the payer's place is untouched.
    if (paymentParam === 'cancelled' || !sessionId) {
      setOutcome('unpaid');
      cb.current.onFailure?.({ reason: 'cancelled' });
      strip();
      return;
    }

    let dropped = false;
    setVerifying(true);
    paymentsAPI.getCheckoutStatus(sessionId)
      .then((res) => {
        if (dropped) return;
        if (res?.paid) {
          setOutcome('paid');
          // Before the payer is offered anywhere to go: whatever the caller
          // does with a completed payment — reload a roster, mark a place
          // confirmed — must have happened by the time they can leave.
          cb.current.onSuccess?.(res);
        } else {
          setOutcome('unpaid');
          cb.current.onFailure?.({ reason: res?.paymentStatus || 'unpaid' });
        }
      })
      .catch(() => {
        if (dropped) return;
        // Ours or Stripe's, not something the payer can act on. Treated as
        // unpaid so nothing is settled on a guess — the webhook is what
        // settles a payment that did in fact go through.
        setOutcome('unpaid');
        cb.current.onFailure?.({ reason: 'unverified' });
      })
      .finally(() => { if (!dropped) { setVerifying(false); strip(); } });

    return () => { dropped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentParam, sessionId]);

  // ─── Going to Stripe ─────────────────────────────────────────────────────
  const pay = useCallback(async () => {
    setBusy(true);
    setOutcome(null);
    try {
      // NOTHING TO PAY, SO NOTHING TO PAY WITH.
      //
      // No Stripe session — a zero-value one is not a thing — and no counter
      // payment either, because nobody is going to hand over nothing at a
      // desk. The order is completed directly, which writes whatever it was
      // raised to create, exactly as a settled payment would.
      if (orderIds.length && nothingToPay) {
        // Every one of them. Sequentially rather than in parallel: these write
        // places, and a burst of concurrent settles on one tournament is how
        // two of them race for the last seat.
        for (const id of orderIds) await paymentsAPI.completeFreeOrder(id);
        setOutcome('paid');
        // Same contract as a real payment: whatever the caller does with a
        // completed one — reload a roster, mark a place confirmed — has to
        // have happened before the payer is offered anywhere to go.
        cb.current.onSuccess?.({ paid: true, free: true, orderId });
        setBusy(false);
        return;
      }

      // A registration pays for itself — no order is raised here.
      if (!orderId && registrationIds?.length) {
        if (embeddedEnabled) { setEmbeddedRegistration({ kind, ids: registrationIds }); setBusy(false); return; }
        const here = `${window.location.pathname}${window.location.search}`;
        const { url } = await paymentsAPI.createCheckoutSession({
          kind, registrationIds, returnPath: here, cancelPath: here,
        });
        if (!url) throw new Error('Stripe did not return a checkout URL');
        window.location.assign(url);
        return;
      }

      let ids = orderIds;
      if (!ids.length) {
        /* NAME THE REGISTRANT. The server refuses a line that does not say who
         * it is for, and this screen knows: `registrantId` is null for "my
         * account", which is a real answer rather than an absent one. */
        const raised = await paymentsAPI.startBooking({
          items: items.map(i => ({ ...i, playerId: i.playerId ?? registrantId ?? null })),
          paymentMethod: method,
        });
        ids = [raised.id];
      }

      if (method !== 'card') {
        // NOT swallowed any more. Reserving now takes the place as well as
        // naming a method, so this can fail for a real reason — the last seat
        // went while they were deciding — and a caller told "reserved" who has
        // no seat is the bug this whole path just had.
        // Each entrant's place, one at a time. Reserving takes the place now,
        // so a failure part-way through has really happened to some of them —
        // it is reported rather than swallowed, and the ones already held stay
        // held rather than being unwound into nobody knowing what is true.
        for (const each of ids) await paymentsAPI.setOrderIntent(each, method);
        setOutcome('reserved');
        cb.current.onSuccess?.({ settledBy: 'counter', orderId: ids[0], orderIds: ids });
        return;
      }

      // Mount Stripe on this page instead of leaving for it. Same order, same
      // webhook, same outcome — the only difference is where the card is typed.
      // The redirect stays the fallback, so an academy with the flag off is
      // unaffected and this page works either way.
      if (embeddedEnabled) {
        setEmbeddedOrderIds(ids);
        setBusy(false);
        return;
      }

      // This very page, so the return is a navigation rather than a rescue.
      // window.location is right here: it is the address Stripe will send the
      // browser back to, not a router path.
      const here = `${window.location.pathname}${window.location.search}`;
      // ONE charge for all of them. The server already loads and settles a
      // list, so several entrants are one payment and one card statement line
      // — and one payment intent, which is what later lets a single withdrawal
      // refund one share of it.
      const { url } = await paymentsAPI.createCheckoutSession({
        kind: 'order',
        registrationIds: ids,
        returnPath: here,
        cancelPath: here,
      });
      if (!url) throw new Error('Stripe did not return a checkout URL');
      window.location.assign(url);
    } catch (err) {
      setError(err?.message || 'Could not start payment');
      setBusy(false);
    }
  }, [JSON.stringify(orderIds), items, method, embeddedEnabled, kind, registrationIds, nothingToPay]);


  /**
   * Re-pin to the first step while the payer has not moved.
   *
   * MUST live above the early returns below. React counts hooks by position, so
   * an effect after `if (busy) return …` runs on some renders and not others —
   * pressing Pay took the redirect branch, skipped this hook, and brought the
   * whole tree down with "Rendered fewer hooks than expected".
   *
   * The step list is derived here rather than read from `steps`, which is not
   * built until after those returns.
   */
  useEffect(() => {
    if (touched) return;
    const first = collectRegistrant ? 'registrant'
                : infoFields.length ? 'info'
                : needsSigning      ? 'agreements'
                : nothingToPay      ? 'review'
                : 'method';
    if (first !== step) setStep(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectRegistrant, infoFields.length, needsSigning, nothingToPay, touched]);

  // ─── Screens ─────────────────────────────────────────────────────────────


  // Leaving for Stripe is a whole-page event, so the page says so.
  //
  // Left as it was, the summary and the method list stayed on screen and
  // interactive while the redirect was already under way, and the browser then
  // blanked them a moment later on its way to stripe.com — which read as a
  // flicker, and left a Pay button that could be pressed twice.
  if (busy && method === 'card') {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 py-20 ${theme.textMuted}`}>
        <Loader2 size={22} className="animate-spin" />
        <p className="text-sm">Taking you to Stripe to pay securely…</p>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 py-20 ${theme.textMuted}`}>
        <Loader2 size={22} className="animate-spin" />
        <p className="text-sm">Confirming your payment with Stripe…</p>
      </div>
    );
  }

  if (outcome === 'paid' || outcome === 'reserved') {
    return (
      <div className="py-10 text-center space-y-5">
        <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
          <Check size={26} className="text-green-600" />
        </div>
        <div>
          <h2 className={`text-xl font-bold ${theme.text}`}>
            {nothingToPay ? 'You are all set'
              : outcome === 'reserved' ? 'Your place is reserved'
              : 'Payment successful'}
          </h2>
          <p className={`text-sm mt-1 ${theme.textSecondary}`}>
            {/* This is the screen someone reads to check it worked, so it says
                what actually happened. Nothing was paid for a free place and
                no receipt is sent; a counter reservation has taken the place
                but not the money, and telling somebody they have paid when
                they still owe it at the door is how they arrive surprised. */}
            {nothingToPay
              ? 'Your place is confirmed. There was nothing to pay.'
              : outcome === 'reserved'
                ? `Your place is held. ${money(subtotal, currency)} is due at the academy.`
                : `${money(total, currency)} paid. A receipt is on its way to you.`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
          <button onClick={onBackAfterPaying || onBack} data-testid="checkout-back-paid"
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center justify-center gap-2`}>
            <ChevronLeft size={15} /> Back to {backTo || 'where you were'}
          </button>
          <button onClick={onHome} className={`${btn.base} ${btn.md} ${theme.buttonSecondary} flex items-center justify-center gap-2`}>
            <Home size={15} /> Home
          </button>
        </div>
      </div>
    );
  }

  // Once Stripe is mounted it owns the screen. Leaving the summary and the Pay
  // button underneath gave the old dialog two Pay buttons and a basket that
  // could still be edited against a session already priced.
  const embedded = embeddedRegistration || (embeddedOrderIds?.length ? { kind: 'order', ids: embeddedOrderIds } : null);
  if (embedded) {
    return (
      <div className="space-y-4">
        <h1 className={`text-2xl font-bold ${theme.text}`}>{title}</h1>
        <PaymentEmbeddedCheckout
          kind={embedded.kind}
          registrationIds={embedded.ids}
          returnPath={`${window.location.pathname}${window.location.search}`}
          onError={(err) => { setEmbeddedOrderId(null); setEmbeddedRegistration(null); setError(err?.message || 'Card payment could not start'); }}
          onClose={() => { setEmbeddedOrderId(null); setEmbeddedRegistration(null); }}
        />
        <button onClick={() => { setEmbeddedOrderId(null); setEmbeddedRegistration(null); }} className={`w-full text-sm ${theme.textMuted} hover:underline py-1`}>
          Choose another way to pay
        </button>
      </div>
    );
  }

  // ─── The layout ──────────────────────────────────────────────────────────
  //
  // Steps on the left, the money on the right, the way every checkout worth
  // copying does it. A completed step collapses to one line with a Change
  // link, so the page stays short and the payer can still see — and revisit —
  // every answer they have given.
  //
  // On a phone there is no room for two columns, so the summary goes ABOVE the
  // steps: what it costs is the thing people check before they start, and
  // burying it under the form means scrolling past the work to find the price.
  const steps = [
    ...(collectRegistrant ? [{
      id: 'registrant',
      title: 'Who is this for',
      done: () => (registrantId === null ? 'My account' : (registrantLabel || 'Selected')),
    }] : []),
    ...(infoFields.length ? [{
      id: 'info',
      title: 'Additional information',
      done: () => {
        const answered = infoFields.filter(f => {
          const v = info[f.id];
          return v !== undefined && v !== null && v !== '' && v !== false;
        }).length;
        return `${answered} of ${infoFields.length} answered`;
      },
    }] : []),
    ...(needsSigning ? [{
      id: 'agreements',
      title: 'Terms and waiver',
      done: () => (signed.acceptedName ? `Signed by ${signed.acceptedName}` : 'Accepted'),
    }] : []),
    ...(nothingToPay ? [] : [{ id: 'method', title: 'How you will pay', done: () => methodLabel }]),
    { id: 'review', title: nothingToPay ? 'Review and confirm' : 'Review and pay', done: () => null },
  ];
  const currentIndex = Math.max(0, steps.findIndex(x => x.id === step));


  const Summary = (
    <div className={`${theme.card} border rounded-xl divide-y ${theme.border}`}>
      {loading ? (
        <div className={`flex items-center gap-2 py-8 justify-center ${theme.textMuted}`}>
          <Loader2 size={16} className="animate-spin" /> <span className="text-sm">Pricing…</span>
        </div>
      ) : (
        <>
          {/* Named, because this is the panel a payer actually reads before
              paying — and where an order raised for yourself once announced it
              was for your child, in small grey letters on a step nobody
              re-reads. A test asserting on it needs to find it. */}
          <div className="p-3" data-testid="checkout-summary">
            <p className={`text-sm font-semibold ${theme.text}`}>Order summary</p>
            {payingForLabel && (
              <p className={`text-xs mt-0.5 ${theme.textSecondary}`}>For {payingForLabel}</p>
            )}
          </div>
          {lines.map((l, i) => (
            <div key={l.id || i} className="flex items-center justify-between gap-3 p-3">
              <span className={`text-sm min-w-0 flex-1 break-words ${theme.text}`}>{l.priceLabel || l.label}</span>
              <span className={`text-sm font-medium flex-shrink-0 whitespace-nowrap ${theme.text}`}>{money(l.amount, currency)}</span>
            </div>
          ))}
          {fees > 0 && (
            <div className="flex items-center justify-between gap-3 p-3" data-testid="checkout-card-fee">
              <span className={`text-sm ${theme.textSecondary}`}>Online booking fee</span>
              <span className={`text-sm ${theme.textSecondary}`}>{money(fees, currency)}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 p-3">
            <span className={`text-sm font-semibold ${theme.text}`}>Total</span>
            <span className={`text-base font-bold ${theme.text}`}>{money(total, currency)}</span>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Above everything, because it is the reason they are looking at this
          screen a second time. Nothing has been charged and nothing settled. */}
      {outcome === 'unpaid' && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Payment not completed</p>
            <p className="text-sm mt-0.5 opacity-90">
              Nothing has been charged and your place is still held. Try again, or pay another way.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-red-200 bg-red-50 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <h1 className={`text-2xl font-bold ${theme.text}`}>{title}</h1>

      <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
        {/* Summary: first on a phone, second column on a wide screen. */}
        <div className="lg:col-span-2 lg:order-last lg:sticky lg:top-6">{Summary}</div>

        <div className="lg:col-span-3 space-y-3">
          {steps.map((st, i) => {
            const isCurrent = i === currentIndex;
            const isDone    = i < currentIndex;
            return (
              <div key={st.id} data-testid={`checkout-step-${st.id}`}
                   className={`${theme.card} border rounded-xl ${isCurrent ? theme.borderAccent : theme.border}`}>
                <div className="flex items-center justify-between gap-3 p-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0 ${
                      isDone ? 'bg-green-600 text-white'
                             : isCurrent ? theme.tabActive
                             : `${theme.backgroundSecondary} ${theme.textMuted}`}`}>
                      {isDone ? <Check size={12} /> : i + 1}
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-sm font-semibold ${isCurrent || isDone ? theme.text : theme.textMuted}`}>
                        {st.title}
                      </span>
                      {/* The answer stays visible once given — a collapsed step
                          that shows nothing is just a hidden step. */}
                      {isDone && st.done() && (
                        <span className={`block text-xs ${theme.textSecondary}`}>{st.done()}</span>
                      )}
                    </span>
                  </div>
                  {isDone && (
                    <button onClick={() => advance(st.id)} data-testid={`checkout-change-${st.id}`}
                            className={`text-xs font-semibold ${theme.link} hover:underline flex-shrink-0`}>
                      Change
                    </button>
                  )}
                </div>

                {isCurrent && (
                  <div className={`px-3 pb-3 border-t ${theme.border} pt-3`}>
                    {st.id === 'registrant' && (
                      <>
                        <PaymentRegistrantSelect
                          takenIds={takenIds}
                          provisionalIds={provisionalIds}
                          value={registrantId}
                          onChange={(id, label) => {
                            setRegistrantId(id);
                            setRegistrantLabel(label || null);
                            onRegistrantChange?.(id, label);
                          }}
                          heading=""
                          subheading="Choose yourself or one of the players you manage."
                        />
                        <button onClick={() => advance(infoFields.length ? 'info' : (needsSigning ? 'agreements' : nextAfterForms), { registrantUserId: registrantId || null })}
                                // Nobody selected means nobody selectable — the
                                // list said so above, and there is nothing to
                                // carry forward.
                                // A backstop only: the default above is a real
                                // selection, so this cannot fire today. It keeps
                                // the step honest if a caller ever hands in an
                                // unset registrant again.
                                disabled={registrantId === undefined}
                                data-testid="checkout-registrant-continue"
                                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} w-full mt-3`}>
                          Continue
                        </button>
                      </>
                    )}

                    {st.id === 'info' && (
                      <>
                        <PaymentAdditionalInfo
                          fields={infoFields}
                          values={info}
                          onChange={(next) => { setInfo(next); onInfoChange?.(next); }}
                          askedBy={infoAskedBy}
                        />
                        <button
                          onClick={() => advance(needsSigning ? 'agreements' : nextAfterForms, { responses: info })}
                          disabled={missingRequired(infoFields, info).length > 0}
                          data-testid="checkout-info-continue"
                          className={`${btn.base} ${btn.md} ${theme.buttonPrimary} w-full mt-3 disabled:opacity-40`}
                        >
                          Continue
                        </button>
                      </>
                    )}

                    {st.id === 'agreements' && (
                      <>
                        <PaymentAgreements
                          agreement={agreement}
                          value={signed}
                          onChange={setSigned}
                        />
                        <button
                          onClick={async () => {
                            // Recorded BEFORE the money moves. A signature that
                            // only lands after a successful payment is missing
                            // for everyone who pays at the desk, and missing
                            // altogether if the card is refused.
                            if (subjectType && subjectId) {
                              await paymentsAPI.acceptOfferingAgreements(subjectType, subjectId, {
                                forUserId: registrantId || null,
                                orderId:   orderId || null,
                                ...signed,
                              }).catch((err) => setError(err?.message || 'Could not record your acceptance'));
                            }
                            advance(nextAfterForms, {
                              termsAccepted:  !!signed.termsAccepted,
                              waiverAccepted: !!signed.waiverAccepted,
                              acceptedName:   signed.acceptedName || null,
                            });
                          }}
                          disabled={!agreementsSatisfied(agreement, signed)}
                          data-testid="checkout-agreements-continue"
                          className={`${btn.base} ${btn.md} ${theme.buttonPrimary} w-full mt-3 disabled:opacity-40`}
                        >
                          Continue
                        </button>
                      </>
                    )}

                    {st.id === 'method' && (
                      <>
                        <div className="space-y-2">
                          {methods.map(({ id, Icon, label, hint }) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => setMethod(id)}
                              // Same treatment as the registrant list: themed,
                              // and said twice — border and tick — so the chosen
                              // method is obvious rather than inferred.
                              className={`w-full text-left p-3 rounded-xl border-2 transition flex items-center gap-3 ${
                                method === id ? `${theme.buttonSecondary} font-semibold`
                                              : `${theme.card} ${theme.border} ${theme.cardHover}`
                              }`}
                            >
                              <Icon size={16} className={`flex-shrink-0 ${method === id ? '' : theme.textMuted}`} />
                              <span className="min-w-0 flex-1">
                                <span className={`block text-sm font-medium ${method === id ? '' : theme.text}`}>{label}</span>
                                <span className={`block text-xs ${method === id ? '' : theme.textMuted}`}>{hint}</span>
                              </span>
                              {method === id && (
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${theme.primary}`}>
                                  <Check size={12} />
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => advance('review')} data-testid="checkout-method-continue"
                                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} w-full mt-3`}>
                          Continue
                        </button>
                      </>
                    )}

                    {st.id === 'review' && (
                      <>
                        <p className={`text-sm ${theme.textSecondary}`}>
                          {nothingToPay
                            ? 'There is nothing to pay for this. Confirming completes your place.'
                            : method === 'card'
                              ? `You will be taken to Stripe to pay ${money(total, currency)} securely.`
                              : `Nothing is charged now — ${money(subtotal, currency)} is settled at the academy.`}
                        </p>
                        <button
                          onClick={pay}
                          disabled={busy || lines.length === 0}
                          data-testid="checkout-pay"
                          className={`${btn.base} ${btn.md} ${theme.buttonPrimary} w-full mt-3 flex items-center justify-center gap-2`}
                        >
                          {busy ? <Loader2 size={15} className="animate-spin" />
                            : nothingToPay ? <Check size={15} />
                            : method === 'card' ? <CreditCard size={15} /> : <Receipt size={15} />}
                          {nothingToPay
                            ? 'Confirm my place'
                            : method === 'card'
                              ? `Pay ${money(total, currency)}`
                              : `Reserve — ${money(subtotal, currency)} at the desk`}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <button onClick={onBack} data-testid="checkout-cancel"
                  className={`w-full text-sm ${theme.textMuted} hover:underline py-1`}>
            Cancel and go back
          </button>
        </div>
      </div>
    </div>
  );
}
